import classNames from 'classnames';
import React, { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { createSearchParams, useNavigate, useSearchParams } from 'react-router-dom';
import packageJSON from '../../../package.json';
import Button from '../../components/Button';
import Icon, { Icons } from '../../components/Icon/Icon';
import InlineNotice from '../../components/InlineNotice';
import Input from '../../components/Input';
import ListItem from '../../components/ListItem';
import PageHeader from '../../components/PageHeader';
import QrScanner from '../../components/QrScanner';
import StickyPanel from '../../components/StickyPanel';
import AppConfig from '../../config';
import { t } from '../../lang';
import HelperService from '../../services/HelperService';
import RequestService from '../../services/RequestService';
import StorageService from '../../services/StorageService';
import TypesService from '../../services/TypesService';
import WebSocketService from '../../services/WebSocketService';
import { appSlice } from '../../store/slices';
import './AmlCheck.scss';

const BlockchainsById = TypesService.BlockchainsById;
const SIZE_CHEVRON = 8;
const SIZE_XMARK = 20;
const CREDIT_CURRENCY = packageJSON?.creditCurrency ?? 'Credits';

const { routes } = AppConfig;

const AmlCheck = () => {
	const intl = useIntl();
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();

	const quotes = useSelector(store => store.app.quotes);
	const billingBalance = useSelector(store => store.app.billingBalance);
	const billingRate = quotes.find(quote => quote.Symbol === 'Credits')?.Rate || 0;
	const billingBalanceUsd = billingBalance * billingRate;

	const addressParam = searchParams.get('address');
	const gotIt = searchParams.get('gotIt');

	const [address, setAddress] = useState('');
	const [network, setNetwork] = useState(null);
	const [networkManual, setNetworkManual] = useState('');
	const [price, setPrice] = useState(0);
	const [loadingCheck, setLoadingCheck] = useState(false);
	const [error, setError] = useState(false);

	const deleteAddress = () => {
		setError(false);
		setAddress('');
		setNetworkManual('');
		setNetwork('');
	};

	const checkAddress = useCallback(
		addressProp => {
			const addressBlockchainSymbol = HelperService.getBlockchainByAddress(addressProp);

			setNetworkManual('');
			setNetwork('');

			if (!addressBlockchainSymbol) {
				return setError(true);
			}

			const symbolNeedCorrectBlockchain = TypesService.symbolsNeedCorrectBlockchain[addressBlockchainSymbol?.symbol];

			symbolNeedCorrectBlockchain ? setNetworkManual(symbolNeedCorrectBlockchain) : setNetwork(addressBlockchainSymbol);
		},
		[address]
	);

	useEffect(() => {
		if (addressParam) {
			setAddress(addressParam);
			checkAddress(addressParam);
		}
	}, [addressParam]);

	const openSelectNetworkModal = () => {
		dispatch(
			appSlice.actions.addModal({
				tag: 'SelectNetwork',
				outsideParams: { networkManual, setNetwork },
			})
		);
	};

	const getAmlServicePrice = useCallback(async () => {
		const res = await RequestService.GetAmlServicePrice();

		setPrice(res.Amount);
	}, []);

	useEffect(() => {
		StorageService.UpdateBillingBalance();
	}, []);

	useEffect(() => {
		getAmlServicePrice();
	}, [getAmlServicePrice]);

	const checkAML = async () => {
		setLoadingCheck(true);
		dispatch(appSlice.actions.setWsAmlResult(null));
		WebSocketService.RemoveCustomeHandler('handlerAML');

		const handlerAML = message => {
			if (message?.type !== 'MobileAddressAmlResultMsg') return;

			const amlResult = message?.msg?.addressAmlResult;
			if (!amlResult) return;

			if (amlResult.address !== address) return;

			dispatch(appSlice.actions.setWsAmlResult(amlResult));
		};

		WebSocketService.AddCustomHandler(handlerAML);

		const res = await RequestService.CheckAmlAddress(address, network?.network || network?.Name);

		if (res.Status === 'Error') {
			if (res.ErrorCode === 'InvalidFormatError') {
				HelperService.showNotification({
					type: 'error',
					title: t('The address does not match the selected network'),
					description: t('Choose the correct blockchain.'),
				});
			} else {
				const errorMessage = res.ErrorMessage || res.ErrorCode || 'Error';
				RequestService.BaseErrorFunc({
					response: {
						data: {
							title: errorMessage,
						},
					},
				});
			}
			setLoadingCheck(false);
			return;
		}

		// if (res.Status === 'Error') return setLoadingCheck(false);

		navigate({
			pathname: routes.amlCheckResult,
			search: createSearchParams({ address, blockchain: network.network, gotIt: gotIt }).toString(),
		});

		await HelperService.Sleep(300); // for rout animation

		setLoadingCheck(false);
	};

	return (
		<div className='AmlCheck'>
			<StickyPanel
				controls={
					<Button
						onClick={checkAML}
						iconBefore='AML_SCAN'
						styleType='green-dark'
						disabled={!address || !network || loadingCheck || error || billingBalance < price}
					>
						{t('AML check V2')}
					</Button>
				}
			>
				<PageHeader header={<div>{t('AML check')}</div>} />

				{/* {HelperService.showAllIcons()} */}

				<div className='AmlCheck__body'>
					<div className='AmlCheck__input-title'>{t('Address for AML check')}</div>
					<Input
						className={'AmlCheck__input FormBlock'}
						placeholder={intl.formatMessage({ id: 'Address' })}
						value={address}
						onChange={e => {
							setError(false);
							setAddress(e.target.value);
						}}
						onBlur={e => checkAddress(e.target.value)}
						onPaste={e => checkAddress(e.clipboardData.getData('text'))}
						error={error}
						postfix={
							address && (
								<Icon type={Icons.XMARK_CIRCLE_FILL} width={SIZE_XMARK} height={SIZE_XMARK} onClick={deleteAddress} />
							)
						}
					/>
					<ListItem
						className={classNames('FormBlock AmlCheck__network', { disable: !network && !networkManual })}
						title={network?.network || t('Network')}
						description={network ? '' : t('Auto-detected. Select manually if needed.')}
						icon={network?.Icon}
						trailing={network ? [] : [<Icon type={Icons.CHEVRON} width={SIZE_CHEVRON} />]}
						onSelect={() => networkManual && openSelectNetworkModal()}
					/>
					{networkManual && !network && (
						<InlineNotice className='AmlCheck__notification' type='notification' icon='EXCLAMATIONMARK_CIRCLE'>
							{t('The network could not be detected automatically.\nPlease select the network manually.')}
						</InlineNotice>
					)}

					<div className='AmlCheck__margin'></div>

					{address ? (
						<Button className='AmlCheck__reset' styleType='white' onClick={deleteAddress}>
							{t('Reset')}
						</Button>
					) : (
						<QrScanner
							updateValue={text => {
								setError(false);
								checkAddress(text.trim());
								setAddress(text.trim());
							}}
							className='AmlCheck__reset'
							element={
								<Button iconBefore='VIEWFINDER_QRCODE' styleType='white'>
									{t('Scan address')}
								</Button>
							}
						/>
					)}

					<div
						className={classNames('AmlCheck__credit', {
							AmlCheck__insufficient_balance: billingBalance < price,
						})}
					>
						<ListItem
							className='FormBlock'
							icon={CREDIT_CURRENCY}
							iconLabel={'GAS_PUMP_RIM'}
							title={CREDIT_CURRENCY}
							description={AppConfig?.name + ' USD'}
							rightTitle={HelperService.FormatFiatWithoutCurrency(billingBalance)}
							rightDescription={'$' + HelperService.FormatFiatWithoutCurrency(billingBalanceUsd)}
							bottomSeparator={billingBalance < price}
						/>

						{billingBalance < price && (
							<>
								<div className='AmlCheck__insufficient_balance-title'>
									{t('Insufficient balance for this payment methods.')}
								</div>

								<Button onClick={() => navigate(routes.billingCurrency)} styleType='secondary'>
									{t('Top up')} {CREDIT_CURRENCY}
								</Button>
							</>
						)}
					</div>
					<div className='AmlCheck__price-title'>{t('Price per check')}</div>
					<div className='AmlCheck__price'>
						{price} {CREDIT_CURRENCY}
					</div>
				</div>
			</StickyPanel>
		</div>
	);
};

export default AmlCheck;
